
import React, { useState, useEffect } from 'react';
import { Smartphone, Download, X, Share } from 'lucide-react';

export const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    if ((window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches) {
      setIsStandalone(true);
      return;
    }

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Standard PWA prompt listener (Android/Chrome/Edge)
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsVisible(true);
    });

    // For iOS, we show it manually after a short delay
    if (ios) {
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  if (!isVisible || isStandalone) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:bottom-8 z-[100] animate-in slide-in-from-bottom-8 duration-500 no-print">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-5 md:w-80 space-y-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-2 rounded-xl text-white">
              <Smartphone size={20} />
            </div>
            <div>
              <h4 className="font-bold text-slate-900 leading-tight">Install Timesheet Pro</h4>
              <p className="text-xs text-slate-500">Access your dashboard faster</p>
            </div>
          </div>
          <button onClick={() => setIsVisible(false)} className="text-slate-300 hover:text-slate-500">
            <X size={20} />
          </button>
        </div>

        {isIOS ? (
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <p className="text-xs text-slate-600 leading-relaxed">
              To save this app to your home screen:
            </p>
            <div className="flex items-center gap-3 text-sm text-slate-800 font-medium">
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <Share size={16} className="text-blue-600" />
              </div>
              <span>Tap the "Share" icon</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-800 font-medium">
              <div className="bg-white p-2 rounded-lg border border-slate-200 text-lg">
                +
              </div>
              <span>Select "Add to Home Screen"</span>
            </div>
          </div>
        ) : (
          <button 
            onClick={handleInstallClick}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-2 transition active:scale-95"
          >
            <Download size={18} /> Install App
          </button>
        )}
      </div>
    </div>
  );
};
